import React from 'react';
import { 
  TableRow, 
  TableCell, 
  IconButton,
  Chip,
  Tooltip,
  Box,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Account } from '../services/AccountsService';
import { Link as RouterLink, useLocation } from 'react-router-dom';

// Table Row Component
const AccountRow: React.FC<{
  account: Account;
  handleEdit: (id: number) => void;
  formatDate: (date: Date | string) => string;
}> = React.memo(({ account, handleEdit, formatDate }) => {
  const theme = useTheme();
  const location = useLocation();

  // Per-line-of-business chip styling. B2B keeps its original blue; AFF keeps the
  // original red/violet (the previous non-B2B default); EFG is white; AGG and STA
  // get their own distinct colors. Unknown values fall back to the AFF style.
  const lobChipStyles: Record<string, { backgroundColor: string; color: string; border?: string }> = {
    B2B: {
      backgroundColor: alpha(theme.palette.info.main, 0.1),
      color: theme.palette.info.dark
    },
    EFG: {
      backgroundColor: alpha(theme.palette.grey[500], 0.15),
      color: theme.palette.text.primary,
      border: `1px solid ${alpha(theme.palette.grey[500], 0.4)}`
    },
    AFF: {
      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
      color: theme.palette.secondary.dark
    },
    AGG: {
      backgroundColor: alpha(theme.palette.warning.main, 0.15),
      color: theme.palette.warning.dark
    },
    STA: {
      backgroundColor: alpha(theme.palette.success.main, 0.12),
      color: theme.palette.success.dark
    }
  };
  const lobStyle = lobChipStyles[account.lineOfBusiness] ?? lobChipStyles.AFF;

  return (
    <TableRow 
      hover 
      sx={{ 
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: `${alpha(theme.palette.primary.light, 0.08)} !important`,
          transform: 'translateY(-1px)'
        }
      }}
    >
      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'text.secondary' }}>
        {account.id}
      </TableCell>
      <TableCell>
        <Typography variant="body1" fontWeight={500} sx={{ color: theme.palette.primary.main }}>
          {account.name}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={account.lineOfBusiness}
          size="small"
          sx={{
            fontWeight: 500,
            ...lobStyle,
            borderRadius: '8px'
          }}
        />
      </TableCell>
      <TableCell align="center">
        {account.strategyPortalEnabled ? (
          <Tooltip title="Enabled">
            <Box sx={{ display: 'inline-flex' }}>
              <CheckCircleIcon 
                color="success" 
                sx={{ 
                  filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.1))',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                }} 
              />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Disabled">
            <Box sx={{ display: 'inline-flex' }}>
              <CancelIcon 
                color="error" 
                sx={{ 
                  filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.1))',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                }} 
              />
            </Box>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {account.supersetRole || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatDate(account.lastUpdated)}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Tooltip title="Edit Account">
            <IconButton
              component={RouterLink}
              to={`/accounts/${account.id}/edit`}
              state={{ returnTo: location.pathname + location.search }}
              color="primary"
              sx={{
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  transform: 'scale(1.1)'
                }
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="View Users">
            <IconButton 
              component={RouterLink} 
              to={`/accounts/${account.id}/users`}
              color="info"
              sx={{ 
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  transform: 'scale(1.1)'
                }
              }}
            >
              <PeopleIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="View API Keys">
            <IconButton 
              component={RouterLink} 
              to={`/accounts/${account.id}/api-keys`}
              color="secondary"
              sx={{ 
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  transform: 'scale(1.1)'
                }
              }}
            >
              <VpnKeyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
});

export default AccountRow;